import { cx } from "@emotion/css";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import routing from "routing";
import { isMultiAnswersExam } from "src/helpers/examHelpers";
import { getMaxQuestions } from "src/hooks/useAppConfig";
import { useCreateExam } from "src/hooks/useExams";
import ExamIcon from "src/images/Icons/newIcons/navigation/exams.svg";

import useSearchParams from "../../../hooks/useSearchParams";
import { Header } from "../../Main/ExamsBox/ExamsBox";
import TimerFilter from "../Filters/TimerFilter";
import DefaultExamCreator from "./DefaultExamCreator";

export default function NewExamCreator() {
	const search = useSearchParams();
	const history = useHistory();
	const { t } = useTranslation();
	const { mutateAsync: mutateAsyncExam, isPending: isLoading } =
		useCreateExam();
	const mutateAsync = mutateAsyncExam as unknown as (
		payload: Record<string, unknown>,
	) => Promise<unknown>;
	const [type] = useState(search?.type);
	const initialSelectedFolderIds = search?.folderId ? [search.folderId] : [];

	const extraInitialState = isMultiAnswersExam(type)
		? {
				typeDetails: {
					type,
					timePerQuestion: 60,
				},
			}
		: {};

	useEffect(() => {
		if (!type) {
			history.push(routing.paths.pickExamType);
		}
	}, [history.push, type]);

	const onSubmit = async (
		details: Record<string, unknown>,
		name: string,
		count: number,
		folderIds: string[],
	) => {
		const maxQuestions = getMaxQuestions();
		if (count > maxQuestions) {
			toast.error(t("errors.maxCountOfQuestions", { count: maxQuestions }), {
				position: toast.POSITION.BOTTOM_LEFT,
				pauseOnHover: false,
				autoClose: 5000,
			});
			return;
		}

		await mutateAsync({
			...details,
			name,
			folderIds,
		});
	};

	const renderExtraFiltersBefore = (
		examDetails: unknown,
		setExamDetails: unknown,
		{ examTime }: { examTime: unknown },
	) => {
		if (!isMultiAnswersExam(type)) {
			return null;
		}

		return (
			<div>
				<TimerFilter
					details={examDetails}
					setDetails={setExamDetails}
					examTime={examTime}
				/>
			</div>
		);
	};

	const isDisabled = (
		examDetails: { typeDetails?: { timePerQuestion: number } } | null,
	) => {
		if (examDetails) {
			return examDetails?.typeDetails?.timePerQuestion === 0;
		}

		return false;
	};

	return (
		<DefaultExamCreator
			Header={
				<Header
					className="flex-1"
					title={t("table.exam.title")}
					icon={<ExamIcon className={cx("icon-super-big icon-primary")} />}
					withBackButton
				/>
			}
			inputPlaceholder={t("creators.exams.inputPlaceholder")}
			onSubmit={onSubmit}
			isLoading={isLoading}
			renderExtraFiltersBefore={renderExtraFiltersBefore}
			extraInitialState={extraInitialState}
			isDisabled={isDisabled}
			initialSelectedFolderIds={initialSelectedFolderIds}
		/>
	);
}
